import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { object, string } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, SearchInput, Text } from '@mrshmllw/smores-react'

import { RadioGroupWithAdditionalDataValue } from 'directFlow/components/RadioGroupWithAdditionalData'
import { requiredMessage } from 'directFlow/state/config/content'
import { ModificationItem } from 'directFlow/state/types'
import { NoSearchResultsFound } from 'components/NoSearchResultsFound'

import { OptionItem } from 'types'

const defaultValues: ModificationItem = {
  modifiedPart: '',
}

export const modificationModalSchema = object({
  modifiedPart: string().required(requiredMessage),
})

const resolver = yupResolver(modificationModalSchema)

type AddModificationFormProps = {
  options: OptionItem[]
  initialState?: ModificationItem
  currentModifications?: RadioGroupWithAdditionalDataValue<ModificationItem>
  onSubmit: (values: ModificationItem) => void
}

export const AddModificationForm: FC<AddModificationFormProps> = ({
  options,
  initialState,
  onSubmit,
}) => {
  const { control, handleSubmit, trigger } = useForm<ModificationItem>({
    defaultValues: initialState || defaultValues,
    resolver,
  })

  useEffect(() => {
    if (initialState !== undefined) {
      trigger()
    }
  }, [trigger])

  return (
    <Wrapper>
      <Box>
        <Text color="sesame" mb="24px">
          Choose the modification and click ‘save’. You can then add more if you need to.
        </Text>
        <Controller
          name="modifiedPart"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <SearchInput
              placeholder="Select which part is modified"
              searchList={options.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
              showIcon
              clearSearch
              notFoundComponent={(searchTerm) => (
                <NoSearchResultsFound searchTerm={searchTerm} type="modification" />
              )}
              value={value}
              onFound={onChange}
              onBlur={onBlur}
              error={!!error}
              errorMsg={error?.message}
              data-testid="modifiedPart"
            />
          )}
        />
      </Box>

      <Button primary type="button" onClick={handleSubmit(onSubmit)}>
        Save modification
      </Button>
    </Wrapper>
  )
}

const Wrapper = styled(Box)`
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: 768px) {
    min-height: 320px;
  }
`
